import { Box }                      from '@chakra-ui/react';
import { Input }                    from '@chakra-ui/react';
import { Text }                     from '@chakra-ui/react';
import DeliverableTransferModel     from '@/models/services/shop/DeliverableTransferModel';
import DeliverableTransferTypeModel from '@/models/services/shop/DeliverableTransferTypeModel';
import React                        from 'react';
import { t }                        from 'i18next';
import { ModalData }                from '@/components/modal/Modal';
import { toaster }                  from '@/components/ui/toaster';
import type ApiCollection           from '@widesk-core/models/ApiCollection';
import { CodeKindReference }        from '@/models/services/code/CodeKindModel';

type Params = {
	codeKindReference: CodeKindReference,
	ownerEmail: string;
	deliverableUrn: string;
	deliverableTransferTypeCollection: ApiCollection<DeliverableTransferTypeModel>;
	setModalData: React.Dispatch<React.SetStateAction<ModalData | undefined>>;
	setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	onSuccess?: (email: string) => void;
};

const EMAIL_REGEX = /\S+@\S+\.\S+/;

function isValidEmail(value: string) {
	return EMAIL_REGEX.test(value);
}

export function openTicketTransferModal(params: Params) {
	const {
		codeKindReference,
		ownerEmail,
		deliverableUrn,
		deliverableTransferTypeCollection,
		setModalData,
		setModalIsOpen,
		onSuccess,
	} = params;

	const transferEmailRef = { current: ownerEmail };
	const setTransferEmailErrorRef = { current: (() => {}) as (msg: string | null) => void };

	const TransferTicketBody = ({ initialEmail }: { initialEmail: string }) => {
		const [localEmail, setLocalEmail] = React.useState(initialEmail);
		const [localError, setLocalError] = React.useState<string | null>(null);

		React.useEffect(() => {
			transferEmailRef.current = initialEmail;
			setTransferEmailErrorRef.current = setLocalError;
		}, [initialEmail]);

		const handleChange = (e: any) => {
			const value =
				typeof e === 'string'
					? e
					: e?.currentTarget?.value ?? e?.target?.value ?? '';

			setLocalEmail(value);
			transferEmailRef.current = value;

			if (localError) {
				setLocalError(null);
			}
		};

		return (
			<Box>
				{
					codeKindReference === 'exchange'
						? <Text>{t('pages.ticket.actions.transfer.modal.exchangeMessage', { email: (transferEmailRef.current ?? '').trim() })}</Text>
						: <>

							<Text>{t('pages.ticket.actions.transfer.modal.message')}</Text>

							<Box mt={3} width="50%">
								<Input
									value={localEmail}
									onChange={handleChange}
									aria-invalid={localError ? 'true' : undefined}
									_invalid={{
										borderColor: 'red.400',
										boxShadow: '0 0 0 1px var(--chakra-colors-red-400)',
									}}
									placeholder={t('pages.contacts.forms.contactForm.email')}
								/>

								{localError && (
									<Text color="red.400" fontSize="12px" mt={1}>
										{localError}
									</Text>
								)}
							</Box>
						</>
				}
			</Box>
		);
	};

	setModalData({
		setIsOpen: setModalIsOpen,
		titleComponent: <Text>{t('pages.ticket.actions.transfer.modal.title')}</Text>,
		bodyComponent: <TransferTicketBody initialEmail={ownerEmail} />,
		onClose: () => {
			setModalIsOpen(false);
			setModalData(undefined);
		},
		onConfirm: async () => {
			const email = (transferEmailRef.current ?? '').trim();

			if (!isValidEmail(email)) {
				const msg = t('pages.contacts.forms.contactForm.errors.pattern');

				setTransferEmailErrorRef.current(msg);

				toaster.create({
					title: msg,
					type: 'error',
					duration: 4000,
				});

				throw new Error('Invalid email');
			}

			const deliverableTransferType = (
				await deliverableTransferTypeCollection
					.setFilter('reference', 'no_regeneration')
					.list()
			).first();

			if (!deliverableTransferType) {
				throw new Error('Missing deliverableTransferType');
			}

			const transfer = new DeliverableTransferModel();

			await transfer.patch({
				deliverable: deliverableUrn,
				deliverableTransferType: deliverableTransferType.urn,
				email,
			});
		},
		onError: () => {
			toaster.create({
				title: t('pages.ticket.actions.transfer.error'),
				type: 'error',
				duration: 4000,
			});
		},
		onSuccess: async () => {
			const email = (transferEmailRef.current ?? '').trim();

			toaster.create({
				title: t('pages.ticket.actions.transfer.success', { email }),
				type: 'success',
				duration: 4000,
			});

			onSuccess?.(email);

			setModalIsOpen(false);
			setModalData(undefined);
		},
	});

	setModalIsOpen(true);
}
